<template>
    <div class="py-8 px-4">
        <div class="max-w-full mx-auto">
            <!-- Back Button -->
            <div @click="backToList"
                class="text-lg cursor-pointer flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition duration-150">
                <q-icon name="arrow_back" size="20px" />
                Back
            </div>

            <div class="bg-white px-8 py-4 shadow-md rounded-lg mt-2 border border-gray-100">
                <!-- Header -->
                <div class="text-center mb-5">
                    <h3 class="text-xl font-bold text-gray-800">Postal Entry</h3>
                    <p class="text-sm text-gray-500 mt-2">
                        Select gameweek and up to 6 players
                    </p>
                </div>

                <div class="flex justify-end items-center">
                    <CoundownTimer :deadline="deadlineTime"
                        customClass="px-7 py-2 max-w-[250px] flex justify-center items-center text-center rounded-[4px] bg-black mt-[8px]"
                        titleClass="text-[#FFFFFF] text-[18px] md:text-[18px] lg:text-[20px] font-extrabold transform scale-x-150 md:scale-x-125 lg:scale-x-150"
                        timeClass="text-[#FFFFFF] font-family-leddotmatrix text-[18px] md:text-[25px] lg:text-[30px] md:w-[125px] lg:w-[152px] transform scale-x-150 md:scale-x-[1.3] lg:scale-x-125 mt-[-4px]"
                        bottomTextClass="text-[#FFFFFF] text-sm text-[18px] md:text-[15px] lg:text-[17px] transform scale-x-150 md:scale-x-[1.8] lg:scale-x-[1.8] mt-[-4px]"
                        titleText="TIME TO DEADLINE" bottomText="HRS MINS SECS" />
                </div>

                <div class="mb-8 relative gameweek-container">
                    <label class="block mb-3 font-semibold text-gray-700 text-lg">Current Gameweek</label>
                    <div class="relative">
                        <input type="text" v-model="currentWeek" placeholder="Search or select Gameweek..."
                            class="w-full border rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:border-transparent !text-lg border-gray-300"
                            disabled />
                    </div>
                </div>

                <!-- Player Multi-Select -->
                <div class="mb-8 relative player-container">
                    <label class="block mb-3 font-semibold text-gray-700 text-lg ">Select Players (Max 6)</label>
                    <div tabindex="0" :class="[
                        'border rounded-lg px-4 py-3 flex flex-wrap gap-2 cursor-pointer min-h-[52px] items-center focus:outline-none focus:ring-2 focus:border-transparent text-lg',
                        showValidation
                            ? isPlayersValid
                                ? 'border-gray-300 focus:ring-blue-500'
                                : 'border-red-300 focus:ring-red-500 focus-border-red-500'
                            : 'border-gray-300 focus:ring-blue-500',
                        { 'opacity-50 cursor-not-allowed bg-gray-50': !selectedGameweek || isDeadlinePassed },
                    ]" @click="togglePlayerDropdown" @keydown.enter.space="togglePlayerDropdown">
                        <template v-if="selectedPlayersObjects.length">
                            <span v-for="p in selectedPlayersObjects" :key="p.value"
                                class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center gap-1 text-sm font-medium">
                                {{ p.label }}
                                <button type="button" class="text-blue-600 hover:text-blue-800 font-bold ml-1"
                                    @click.stop="deselectPlayer(p)" :disabled="isDeadlinePassed">
                                    &times;
                                </button>
                            </span>
                        </template>
                        <span v-else class="text-gray-400 flex items-center gap-2">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                            Search and select players...
                        </span>
                        <div v-if="selectedGameweek"
                            class="ml-auto text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                            {{ selectedPlayers.length }}/6
                        </div>
                    </div>

                    <!-- Player Dropdown -->
                    <Transition name="fade">
                        <div v-if="showPlayerDropdown"
                            class="absolute z-20 bg-white border border-gray-300 text-lg rounded-lg mt-1 mb-20 max-h-72 overflow-y-auto w-full shadow-xl player-dropdown">
                            <div class="sticky top-0 bg-white border-b border-gray-200 px-4 py-3">
                                <input type="text" v-model="playerQuery" placeholder="Search players..."
                                    class="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    @click.stop />
                            </div>
                            <div v-for="p in filteredPlayers" :key="p.value"
                                class="px-4 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors">
                                <div @click.stop="selectPlayer(p)" class="flex items-center gap-3 py-3">
                                    <div class="flex items-center justify-center w-5 h-5 rounded border-2 border-gray-300 flex-shrink-0"
                                        :class="{
                                            'bg-blue-500 border-blue-500 text-white': selectedPlayers.includes(p.value),
                                        }">
                                        <svg v-if="selectedPlayers.includes(p.value)" class="h-3 w-3"
                                            fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clip-rule="evenodd"></path>
                                        </svg>
                                    </div>
                                    <span class="text-gray-700 font-medium flex-1">{{ p.label }}</span>
                                    <span class="text-gray-700 font-medium flex-1">{{ p.team }}</span>
                                </div>
                            </div>
                            <div v-if="filteredPlayers.length === 0" class="px-4 py-6 text-center text-gray-500">
                                <svg class="mx-auto h-12 w-12 text-gray-400 mb-2" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                </svg>
                                No players found.
                            </div>
                        </div>
                    </Transition>
                    <p v-if="showValidation && selectedGameweek && !isPlayersValid" class="mt-1 text-sm text-red-600">
                        Please select exactly 6 players.
                    </p>
                </div>

                <!-- ✅ Selected Players Table -->
                <Transition name="slide">
                    <div v-if="selectedPlayers.length === 6" class="mb-8">
                        <div class="flex items-center justify-between mb-4">
                            <h5 class="font-semibold text-gray-700 text-lg">Your Selected Players</h5>
                        </div>
                        <div class="bg-gray-50 rounded-lg overflow-hidden">
                            <table class="min-w-full">
                                <thead>
                                    <tr class="bg-white">
                                        <th
                                            class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                            Player Name
                                        </th>
                                        <th
                                            class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                            Team
                                        </th>
                                        <th
                                            class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                            Action
                                        </th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-200">
                                    <tr v-for="p in selectedPlayersObjects" :key="p.value" class="bg-white">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {{ p.label }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {{ p.team }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button @click="deselectPlayer(p)" class="text-red-600 hover:text-red-900"
                                                :disabled="isDeadlinePassed"
                                                :class="{ 'opacity-50 cursor-not-allowed': isDeadlinePassed }">
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Transition>

                <!-- Buttons -->
                <div class="flex justify-end gap-4 pt-6">
                    <button
                        class="px-10 py-3 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition-colors"
                        @click="closePage">
                        Cancel
                    </button>
                    <button :disabled="loading || isDeadlinePassed"
                        class="px-8 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        @click="submitSelection">
                        <svg v-if="loading" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg"
                            fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                            </circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                        </svg>
                        Submit Entry
                    </button>
                </div>
            </div>

            <div v-if="showPostalSuccess" :class="{
                'fixed inset-0 w-full h-full flex items-center justify-center z-[8000] bg-[rgba(0,0,0,0.6)] bg-opacity-95 overflow-hidden': !$q.screen.lt.sm,
                'fixed inset-0 w-full h-full flex flex-col z-[8000] bg-[rgba(0,0,0,0.6)] bg-opacity-60 overflow-hidden': $q.screen.lt.sm
            }">
                <q-card
                    :class="{ 'px-10 md:px-28 py-16 rounded-xl flex flex-col gap-8 min-w-[50vw]': !$q.screen.lt.sm, 'px-10 md:px-28 py-10 flex flex-col gap-8 min-w-[50vw] h-full justify-between': $q.screen.lt.sm }">
                    <div class="flex items-center justify-between">
                        <p class="text-2xl text-[#4F39F6] font-extrabold">
                            POSTAL ENTRY
                        </p>
                        <q-btn flat round dense icon="close" color="black" @click="closePage" />
                    </div>
                    <div class="flex flex-col items-center text-center">
                        <div class="flex flex-col items-center justify-center mb-2">
                            <SuccessSvg class="w-10 h-10" />
                            <p
                                class="text-[20px] md:text-[26px] lg:text-[28px] font-bold text-[#4F39F6] my-2">
                                Postal Entry Submission
                            </p>
                        </div>
                        <p class="text-[#666666] text-xl text-center my-3 max-w-lg">
                            Postal Entry Submitted Successfully
                        </p>
                    </div>
                    <div>
                        <q-btn label="Okay" type="submit" @click="closePage"
                            class="full-width rounded-xl text-white text-xl font-extrabold"
                            style=" background-color: #4F39F6; font-style: italic" />
                    </div>
                </q-card>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from "vue";
const props = defineProps({
    userId: {
        type: Number,
        required: true,
    },
});
import CoundownTimer from "./CoundownTimer.vue";
import SuccessSvg from "./svg/SuccessSvg.vue";
const emit = defineEmits(["submit", "close", "back"]);

const showGameweekDropdown = ref(false);
const showPlayerDropdown = ref(false);
const gameweekQuery = ref("");
const playerQuery = ref("");
const selectedGameweek = ref(null);
const selectedPlayers = ref([]);
const showValidation = ref(false);
const ignoreGameweekBlur = ref(false);
const loading = ref(false);
const currentWeek = ref(null);
const showPostalSuccess = ref(false);

const allGameweeks = ref([]);
const allPlayers = ref([]);
const deadlineTime = ref(new Date());
const timeToDeadline = ref(0);

const backToList = () => {
    emit("back");
}

const getWeeks = async () => {
    try {
        const response = await fetch("/admin/postal_entry", { method: "GET" });
        const val = await response.json();

        allGameweeks.value = val.game_weeks.map((gw) => ({
            id: gw.id,
            label: gw.label,
            value: gw.week_number,
        }));

        allPlayers.value = val.players.map((p) => ({
            label: p.name,
            team: p.team_code,
            value: p.id,
            position: p.position,
        }));
        console.log(val.time_to_deadline)
        currentWeek.value = `Gameweek ${val.current_week}`;
        timeToDeadline.value = val.time_to_deadline;
        const newDeadline = val.time_to_deadline * 1000;
        deadlineTime.value = new Date(Date.now() + newDeadline);
        const current = allGameweeks.value.find(
            (gw) => gw.value === val.current_week
        );
        if (current) {
            selectedGameweek.value = current;
            gameweekQuery.value = current.label;
        }
    } catch (error) {
        console.error("Error fetching data:", error);
    }
};

const filteredPlayers = computed(() => {
    if (!playerQuery.value) return allPlayers.value;
    const q = playerQuery.value.toLowerCase();
    return allPlayers.value.filter((p) => p.label.toLowerCase().includes(q));
});

const selectedPlayersObjects = computed(() =>
    allPlayers.value.filter((p) => selectedPlayers.value.includes(p.value))
);

const isGameweekValid = computed(() => !!selectedGameweek.value);
const isPlayersValid = computed(() => selectedPlayers.value.length === 6);
const isFormValid = computed(() => isGameweekValid.value && isPlayersValid.value);
const isDeadlinePassed = computed(() => timeToDeadline.value === 0);

const togglePlayerDropdown = () => {
    if (!selectedGameweek.value || isDeadlinePassed.value) return;
    showPlayerDropdown.value = !showPlayerDropdown.value;
    if (showPlayerDropdown.value) {
        nextTick(() => {
            const input = document.querySelector('input[placeholder="Search players..."]');
            input?.focus();
        });
    }
};

const selectPlayer = (p) => {
    if (selectedPlayers.value.includes(p.value)) {
        selectedPlayers.value = selectedPlayers.value.filter((v) => v !== p.value);
    } else if (selectedPlayers.value.length < 6) {
        selectedPlayers.value.push(p.value);
    }
};

const deselectPlayer = (p) => {
    selectedPlayers.value = selectedPlayers.value.filter((v) => v !== p.value);
};

const handleOutsideClick = (event) => {
    const target = event.target;
    if (
        showGameweekDropdown.value &&
        !target.closest(".gameweek-dropdown") &&
        !target.closest(".gameweek-container")
    ) {
        showGameweekDropdown.value = false;
    }
    if (
        showPlayerDropdown.value &&
        !target.closest(".player-dropdown") &&
        !target.closest(".player-container")
    ) {
        showPlayerDropdown.value = false;
    }
};

const submitSelection = async () => {
    showValidation.value = true;
    if (!isFormValid.value) return;

    loading.value = true;
    try {
        const payload = {
            game_week_bet: {
                user_id: props.userId, // <-- pass user_id prop from parent
                game_week_id: selectedGameweek.value.id,
            },
            player_ids: selectedPlayers.value,
        };

        const response = await fetch("/admin/postal_entry", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document
                    .querySelector('meta[name=\"csrf-token\"]')
                    ?.getAttribute("content"),
            },
            body: JSON.stringify(payload),
        });

        const result = await response.json();
        console.log("✅ Postal Entry Response:", result);

        if (response.ok) {
            showPostalSuccess.value = true;
        } else {
            console.error("❌ Server error:", result);
            alert(result.error || result.message || "Failed to submit entry");
        }
    } catch (error) {
        console.error("🚨 Submit error:", error);
        alert("Something went wrong. Please try again later.");
    } finally {
        loading.value = false;
    }
};

const closePage = () => {
    gameweekQuery.value = "";
    playerQuery.value = "";
    selectedPlayers.value = [];
    showGameweekDropdown.value = false;
    showPlayerDropdown.value = false;
    showValidation.value = false;
    showPostalSuccess.value = false;
    // Restore selectedGameweek based on current week if available
    // This ensures the dropdown remains enabled after cancel
    if (allGameweeks.value.length > 0 && currentWeek.value) {
        const weekNumber = parseInt(currentWeek.value.replace("Gameweek ", ""));
        const current = allGameweeks.value.find((gw) => gw.value === weekNumber);
        if (current) {
            selectedGameweek.value = current;
            gameweekQuery.value = current.label;
        }
    } else if (allGameweeks.value.length === 0) {
        // Re-fetch data if it's not available
        getWeeks();
    }
    emit("close");
    backToList();
};

const handleKeydown = (event) => {
    if (event.key === "Escape") {
        showGameweekDropdown.value = false;
        showPlayerDropdown.value = false;
        document.activeElement?.blur();
    }
};

onMounted(() => {
    getWeeks();
    document.addEventListener("click", handleOutsideClick);
    document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
    document.removeEventListener("click", handleOutsideClick);
    document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Modal transitions */
.modal-enter-active {
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-active>div {
    animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-leave-active {
    transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-leave-active>div {
    animation: modalSlideOut 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

@keyframes modalSlideIn {
    from {
        transform: scale(0.9) translateY(-10px);
        opacity: 0;
    }

    to {
        transform: scale(1) translateY(0);
        opacity: 1;
    }
}

@keyframes modalSlideOut {
    from {
        transform: scale(1) translateY(0);
        opacity: 1;
    }

    to {
        transform: scale(0.9) translateY(-10px);
        opacity: 0;
    }
}
</style>
